import axios from 'axios';
import React, { PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';

import { useUserContext } from './user-context';

interface Parameters {
  [key: string]: string;
}

const keysRelevantForUserContext = [
  'immobilienfinanzierung',
  'baufinanzierung',
  'annuitaetendarlehen',
  'baudarlehen',
  'forward-darlehen',
  'bauspardarlehen',
  'hausfinanzierung',
  'hypothekendarlehen',
  'baufinanzierungsrechner',
  'baukostenrechner',
  'tilgungsrechner',
];
const descriptionsRelevantForUserContext = ['content'];

type ThematischerLinkContextValue = {
  parameters: { [key: string]: string };
  setParameter: (key: string, value: string) => void;
  setParameters: (
    parameters: { [key: string]: string } | ((prev: { [key: string]: string }) => { [key: string]: string })
  ) => void;
  updateParameters: (key?: string, description?: string, timeout?: number) => Promise<Parameters>;
};

const ThematicLinkContext = React.createContext<ThematischerLinkContextValue | null>(null);

export { ThematicLinkContext };

export const SESSION_KEY = 'spk.whitelist.entryparameters';

export const ThematicLinkContextProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [parameters, setParameters] = useState<{ [key: string]: string }>({});
  const { entryParameters } = useUserContext() || {};

  const handleSetParameter = useCallback((key: string, value: string) => {
    setParameters((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    if (entryParameters) {
      setParameters((oldParameters) => ({ ...oldParameters, ...entryParameters }));
    }
  }, [entryParameters, setParameters]);

  const updateParameters = useCallback(
    async (key?, description?, timeout = 500) => {
      if (!keysRelevantForUserContext.includes(key) || !descriptionsRelevantForUserContext.includes(description)) {
        return parameters;
      }
      try {
        const { data } = await axios.get('/api/user-context/thematischer-link-parameters', {
          timeout: timeout,
        });
        if (data?.parameters) {
          const { parameters: newParameters } = data;
          setParameters({ ...parameters, ...newParameters });
          return { ...parameters, ...newParameters } as Parameters;
        }
      } catch (error) {
        console.error('Error while fetching thematischer link parameters', error.message);
      }
      return parameters;
    },
    [parameters, setParameters]
  );

  return (
    <ThematicLinkContext.Provider
      value={{ parameters, setParameter: handleSetParameter, setParameters, updateParameters }}
    >
      {children}
    </ThematicLinkContext.Provider>
  );
};

export const useThematicLinkContext = (): ThematischerLinkContextValue | null => {
  return useContext(ThematicLinkContext);
};
